import { useState } from "react";
import { useSession } from "@/auth/session";
import { createCustomer, type CustomerFormPayload } from "@/lib/api";

type CustomerType = "domiciliario" | "empresa" | "institucion";

type Props = {
  onClose: () => void;
  onCreated?: () => void;
};

type FormState = {
  customerType: CustomerType;
  customerCategory: "socio" | "usuario";
  gender: "" | "masculino" | "femenino";
  rut: string;
  dv: string;
  firstName: string;
  lastName: string;
  middleName: string;
  birthDate: string;
  deathDate: string;
  businessName: string;
  businessLine: string;
  address: string;
  phone: string;
  email: string;
  joinDate: string;
  memberNumber: string;
};

const initialForm: FormState = {
  customerType: "domiciliario",
  customerCategory: "usuario",
  gender: "",
  rut: "",
  dv: "",
  firstName: "",
  lastName: "",
  middleName: "",
  birthDate: "",
  deathDate: "",
  businessName: "",
  businessLine: "",
  address: "",
  phone: "",
  email: "",
  joinDate: "",
  memberNumber: "",
};

export default function NuevoClienteModal({ onClose, onCreated }: Props) {
  const { session } = useSession();
  const [form, setForm] = useState<FormState>(initialForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isDomiciliario = form.customerType === "domiciliario";

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!session) {
      setError("Sesion no disponible");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const payload: CustomerFormPayload = {
        customerType: form.customerType,
        customerCategory: form.customerCategory,
        gender: isDomiciliario ? (form.gender || undefined) : undefined,
        rut: form.rut,
        dv: form.dv,
        firstName: isDomiciliario ? form.firstName : undefined,
        lastName: isDomiciliario ? form.lastName : undefined,
        middleName: isDomiciliario ? form.middleName : undefined,
        birthDate: isDomiciliario && form.birthDate ? form.birthDate : undefined,
        deathDate: isDomiciliario && form.deathDate ? form.deathDate : undefined,
        businessName: !isDomiciliario ? form.businessName : undefined,
        businessLine: !isDomiciliario ? form.businessLine : undefined,
        address: form.address,
        phone: form.phone,
        email: form.email,
        joinDate: form.joinDate,
        memberNumber: form.memberNumber || undefined,
      };

      await createCustomer(session, payload);
      onCreated?.();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo crear cliente");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl w-full max-w-2xl shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <h2 className="font-semibold text-gray-800">Nuevo Cliente</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 cursor-pointer">
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[80vh] overflow-y-auto">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Tipo de cliente *</label>
              <select
                value={form.customerType}
                onChange={(e) => setForm({ ...form, customerType: e.target.value as CustomerType })}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
              >
                <option value="domiciliario">Domiciliario</option>
                <option value="empresa">Empresa</option>
                <option value="institucion">Institucion</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Categoria cliente *</label>
              <select
                value={form.customerCategory}
                onChange={(e) => setForm({ ...form, customerCategory: e.target.value as FormState["customerCategory"] })}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
              >
                <option value="socio">Socio</option>
                <option value="usuario">Usuario</option>
              </select>
            </div>

            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">RUT *</label>
              <input required value={form.rut} onChange={(e) => setForm({ ...form, rut: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">DV *</label>
              <input required value={form.dv} onChange={(e) => setForm({ ...form, dv: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
            </div>

            {isDomiciliario ? (
              <>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Nombre *</label>
                  <input required value={form.firstName} onChange={(e) => setForm({ ...form, firstName: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Apellido paterno *</label>
                  <input required value={form.lastName} onChange={(e) => setForm({ ...form, lastName: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Apellido materno</label>
                  <input value={form.middleName} onChange={(e) => setForm({ ...form, middleName: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Fecha nac.</label>
                  <input type="date" value={form.birthDate} onChange={(e) => setForm({ ...form, birthDate: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Genero</label>
                  <select
                    value={form.gender}
                    onChange={(e) => setForm({ ...form, gender: e.target.value as FormState["gender"] })}
                    className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-blue-400"
                  >
                    <option value="">Seleccionar</option>
                    <option value="masculino">Masculino</option>
                    <option value="femenino">Femenino</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Fecha def.</label>
                  <input type="date" value={form.deathDate} onChange={(e) => setForm({ ...form, deathDate: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
                </div>
              </>
            ) : (
              <>
                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Razon social *</label>
                  <input required value={form.businessName} onChange={(e) => setForm({ ...form, businessName: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Giro *</label>
                  <input required value={form.businessLine} onChange={(e) => setForm({ ...form, businessLine: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
                </div>
              </>
            )}

            <div className="col-span-2">
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Direccion principal *</label>
              <input required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Telefono *</label>
              <input required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Email *</label>
              <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Fecha de ingreso *</label>
              <input required type="date" value={form.joinDate} onChange={(e) => setForm({ ...form, joinDate: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-600 mb-1.5">Numero de socio</label>
              <input value={form.memberNumber} onChange={(e) => setForm({ ...form, memberNumber: e.target.value })} className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg" />
            </div>
          </div>

          {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>}

          <div className="flex items-center justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 text-sm font-medium text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200">Cancelar</button>
            <button type="submit" disabled={saving} className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-70">
              {saving ? "Guardando..." : "Guardar Cliente"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
